<template>
    <div>
        <div class="relative flex items-start">
            <div class="flex h-5 items-center">
                <Tooltip :is-disabled="!Boolean(props.disabledExplanation)">
                    <template #trigger="tooltipProps">
                        <input
                            :id="inputId"
                            :popovertarget="tooltipProps.popovertarget"
                            :value="value"
                            :checked="Boolean(value)"
                            :aria-disabled="Boolean(props.disabledExplanation)"
                            type="checkbox"
                            :class="styling"
                            class="size-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-2 aria-disabled:focus-visible:ring-gray-500"
                            :required="required"
                            :aria-invalid="errorMessages && errorMessages.length ? true : undefined"
                            :aria-describedby="getAriaDescribedBy(tooltipProps['aria-labelledby'])"
                            :aria-errormessage="errorMessages && errorMessages.length ? `${inputId}-error` : undefined"
                            @click="onClick"
                            @change="$emit('change', $event)"
                            @blur="$emit('blur', $event)"
                        />
                    </template>
                    <template #text>{{ props.disabledExplanation }}</template>
                </Tooltip>
            </div>
            <div class="ml-3 text-sm">
                <Label
                    :label="props.label"
                    :input-id="inputId"
                    :help-button-text="props.helpButtonText"
                    :is-required="props.required"
                    :is-disabled="Boolean(props.disabledExplanation)"
                />
                <p
                    v-if="description"
                    :id="`${inputId}-info`"
                    :class="[
                        props.status === 'overridden' ? 'mt-2 text-sm text-indigo-600' : 'mt-2 text-sm text-gray-500',
                    ]"
                >
                    {{ description }}
                </p>
            </div>
        </div>
        <div
            v-if="errorMessages && errorMessages.length"
            :id="`${inputId}-error`"
            role="alert"
            class="mt-2 text-sm text-red-600"
        >
            <p v-for="message in errorMessages" :key="message">{{ message }}</p>
        </div>
    </div>
</template>
<script setup lang="ts">
import { nanoid } from 'nanoid';
import { computed } from 'vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import Label from './Label.vue';

type CheckboxFieldProps = {
    value?: boolean | undefined;
    label: string;
    disabledExplanation?: string;
    description?: string;
    helpButtonText?: string;
    required?: boolean;
    errorMessages?: string[];
    status?: 'overridden';
};

const props = defineProps<CheckboxFieldProps>();
const inputId = nanoid();
defineEmits<{ (e: 'change', v: Event): void; (e: 'blur', v: Event): void }>();

function onClick(event: MouseEvent) {
    if (props.disabledExplanation) {
        event.preventDefault();
    }
}

function getAriaDescribedBy(tooltipLabelId: string | undefined): string | undefined {
    const descriptionId = props.description ? `${inputId}-info` : undefined;
    const tooltipId = tooltipLabelId ? tooltipLabelId : undefined;

    return [descriptionId, tooltipId].filter(Boolean).join(' ') || undefined;
}

const styling = computed(() => {
    return [
        'size-4',
        'rounded-sm',
        'focus:ring-indigo-500',
        'border-gray-300',
        'text-indigo-600',
        'aria-disabled:cursor-not-allowed',
        'aria-disabled:border-gray-300',
        'aria-disabled:bg-gray-400',
        'aria-disabled:text-gray-500',
        props.errorMessages && props.errorMessages.length
            ? 'focus:outline-hidden border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
            : 'focus:ring-indigo-500 focus:border-indigo-500 border-gray-300',
        {
            'focus:outline-hidden border-blue-600 text-blue-600 focus:ring-blue-600 focus:border-blue-600':
                props.status === 'overridden',
        },
    ];
});
</script>
